import { Module } from 'vuex';
import { AxiosResponse } from 'axios';
import Chat from '@/services/chat';
import router from '@/router';
import { CHAT_CONVERSATION_ROUTE } from '@/router/routes';
import type {
  ChatConversationDto,
  ChatDirectoryEntry,
  ChatMessageDto,
  ChatNotificationDto,
  CreateChatGroup,
  SendChatMessage,
} from '@/dtos/chat.dto';
import { conversationTitle, employeeDisplayName, plainBody } from '@/utils/chat';
import { closeDesktopNotification, playChime, reflectUnreadInTitle, showDesktopNotification } from '@/utils/chat_alerts';
import {
  APPEND_CHAT_MESSAGE,
  MARK_CHAT_NOTIFICATIONS,
  PATCH_CHAT_CONVERSATION,
  PATCH_CHAT_MESSAGE,
  PREPEND_CHAT_MESSAGES,
  REMOVE_CHAT_CONVERSATION,
  REMOVE_CHAT_MESSAGE,
  RESET_CHAT,
  SET_CHAT_ACTIVE,
  SET_CHAT_CONVERSATIONS,
  SET_CHAT_DIRECTORY,
  SET_CHAT_LOADING,
  SET_CHAT_MEMBER_READ,
  SET_CHAT_MESSAGES,
  SET_CHAT_MESSAGES_LOADING,
  SET_CHAT_NOTIFICATIONS,
  SET_CHAT_NOTIFICATION_COUNTS,
  SET_CHAT_PRESENCE,
  SET_CHAT_SOCKET,
  SET_CHAT_SUMMARY,
  SET_CHAT_TYPING,
  SET_ERRORMSG,
  UPSERT_CHAT_CONVERSATION,
  UPSERT_CHAT_NOTIFICATION,
} from '@/store/mutation_types';
import {
  ADD_CHAT_MEMBERS,
  CHAT_SOCKET_EVENT,
  CLEAR_CHAT,
  CREATE_CHAT_GROUP,
  DELETE_CHAT_MESSAGE,
  DISCARD_CHAT_MESSAGE,
  GET_CHAT_CONVERSATION,
  GET_CHAT_CONVERSATIONS,
  GET_CHAT_DIRECTORY,
  GET_CHAT_MESSAGES,
  GET_CHAT_NOTIFICATIONS,
  GET_CHAT_UNREAD,
  LEAVE_CHAT_CONVERSATION,
  MARK_CHAT_NOTIFICATIONS_READ,
  MARK_CHAT_NOTIFICATIONS_SEEN,
  MARK_CHAT_READ,
  MUTE_CHAT_CONVERSATION,
  OPEN_CHAT_DIRECT,
  REMOVE_CHAT_MEMBER,
  RETRY_CHAT_MESSAGE,
  SEND_CHAT_MESSAGE,
  SET_ACTIVE_CHAT_CONVERSATION,
  UPDATE_CHAT_CONVERSATION,
} from '@/store/action_types';

/* ---------------------------------- State --------------------------------- */

interface MessagePage {
  items: ChatMessageDto[];
  hasMore: boolean;
  cursor: string | null;
  loaded: boolean;
  loading: boolean;
}

interface TypingEntry {
  name: string;
  until: number;
}

export interface ChatState {
  conversations: ChatConversationDto[];
  conversationsLoaded: boolean;
  loading: boolean;
  activeConversationId: string | null;
  /** Details (with members) by conversation id. */
  details: Record<string, ChatConversationDto>;
  messages: Record<string, MessagePage>;
  /** Latest read position of other members, for "Seen" under own messages. */
  reads: Record<string, Record<string, string>>;
  directory: ChatDirectoryEntry[];
  directoryLoadedAt: number;
  directoryLoading: boolean;
  notifications: ChatNotificationDto[];
  notificationsLoaded: boolean;
  unseenCount: number;
  unreadNotificationCount: number;
  presence: Record<string, boolean>;
  typing: Record<string, Record<string, TypingEntry>>;
  socketConnected: boolean;
  everConnected: boolean;
  summary: { unreadTotal: number; mentionTotal: number };
}

const DIRECTORY_TTL_MS = 60 * 1000;
const TYPING_TTL_MS = 5000;

const emptyPage = (): MessagePage => ({ items: [], hasMore: true, cursor: null, loaded: false, loading: false });

const initialState = (): ChatState => ({
  conversations: [],
  conversationsLoaded: false,
  loading: false,
  activeConversationId: null,
  details: {},
  messages: {},
  reads: {},
  directory: [],
  directoryLoadedAt: 0,
  directoryLoading: false,
  notifications: [],
  notificationsLoaded: false,
  unseenCount: 0,
  unreadNotificationCount: 0,
  presence: {},
  typing: {},
  socketConnected: false,
  everConnected: false,
  summary: { unreadTotal: 0, mentionTotal: 0 },
});

const ok = (result: AxiosResponse | undefined | null): boolean => !!(result && result.data && result.data.status);

const sortConversations = (list: ChatConversationDto[]): ChatConversationDto[] =>
  [...list].sort((a, b) => {
    if (a.isGlobal !== b.isGlobal) return a.isGlobal ? -1 : 1;
    return new Date(b.lastMessageAt ?? b.createdAt).getTime() - new Date(a.lastMessageAt ?? a.createdAt).getTime();
  });

const byTime = (a: ChatMessageDto, b: ChatMessageDto): number => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

/** Merges pages and live messages: one entry per id (or clientId while pending), in time order. */
const mergeMessages = (existing: ChatMessageDto[], incoming: ChatMessageDto[]): ChatMessageDto[] => {
  const byId = new Map<string, ChatMessageDto>();
  const byClient = new Map<string, string>();
  const put = (message: ChatMessageDto) => {
    if (message.clientId && byClient.has(message.clientId)) {
      const previousId = byClient.get(message.clientId)!;
      const previous = byId.get(previousId);
      byId.delete(previousId);
      byId.set(message.id, { ...(previous ?? {}), ...message, pending: false, failed: false });
    } else if (byId.has(message.id)) {
      byId.set(message.id, { ...byId.get(message.id)!, ...message });
    } else {
      byId.set(message.id, message);
    }
    if (message.clientId) byClient.set(message.clientId, message.id);
  };
  existing.forEach(put);
  incoming.forEach(put);
  return Array.from(byId.values()).sort(byTime);
};

const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();

const chat: Module<ChatState, any> = {
  state: initialState(),

  getters: {
    chatConversations: (state) => state.conversations,
    chatConversationsLoaded: (state) => state.conversationsLoaded,
    chatLoading: (state) => state.loading,
    chatActiveConversationId: (state) => state.activeConversationId,
    chatActiveConversation: (state) =>
      (state.activeConversationId ? state.details[state.activeConversationId] ?? state.conversations.find((entry) => entry.id === state.activeConversationId) ?? null : null),
    chatConversationById: (state) => (id: string) => state.details[id] ?? state.conversations.find((entry) => entry.id === id) ?? null,
    chatMessagePage: (state) => (id: string) => state.messages[id] ?? emptyPage(),
    chatUnreadTotal: (state) =>
      (state.conversationsLoaded ? state.conversations.reduce((sum, entry) => sum + (entry.unreadCount || 0), 0) : state.summary.unreadTotal),
    chatMentionTotal: (state) =>
      (state.conversationsLoaded ? state.conversations.reduce((sum, entry) => sum + (entry.mentionCount || 0), 0) : state.summary.mentionTotal),
    chatDirectory: (state) => state.directory,
    chatDirectoryLoading: (state) => state.directoryLoading,
    chatNotifications: (state) => state.notifications,
    chatNotificationsLoaded: (state) => state.notificationsLoaded,
    chatUnseenCount: (state) => state.unseenCount,
    chatUnreadNotificationCount: (state) => state.unreadNotificationCount,
    chatPresence: (state) => state.presence,
    chatIsOnline: (state) => (employeeId: string | null | undefined) => !!employeeId && !!state.presence[employeeId],
    chatTyping: (state) => (conversationId: string) => Object.values(state.typing[conversationId] ?? {}).map((entry) => entry.name),
    chatReadAt: (state) => (conversationId: string, employeeId: string) => state.reads[conversationId]?.[employeeId] ?? null,
    chatSocketConnected: (state) => state.socketConnected,
  },

  mutations: {
    [SET_CHAT_LOADING]: (state, loading: boolean) => {
      state.loading = loading;
    },
    [SET_CHAT_CONVERSATIONS]: (state, list: ChatConversationDto[]) => {
      state.conversations = sortConversations(list);
      state.conversationsLoaded = true;
      // details keep their members but take the fresh seat counters
      list.forEach((entry) => {
        if (state.details[entry.id]) state.details[entry.id] = { ...state.details[entry.id], ...entry, members: state.details[entry.id].members };
      });
    },
    [UPSERT_CHAT_CONVERSATION]: (state, conversation: ChatConversationDto) => {
      const { members, ...summary } = conversation;
      const index = state.conversations.findIndex((entry) => entry.id === conversation.id);
      if (index >= 0) state.conversations.splice(index, 1, { ...state.conversations[index], ...summary });
      else state.conversations.push(summary as ChatConversationDto);
      state.conversations = sortConversations(state.conversations);
      if (members !== undefined) state.details = { ...state.details, [conversation.id]: conversation };
      else if (state.details[conversation.id]) state.details = { ...state.details, [conversation.id]: { ...state.details[conversation.id], ...summary } };
    },
    [PATCH_CHAT_CONVERSATION]: (state, patch: Partial<ChatConversationDto> & { id: string }) => {
      const index = state.conversations.findIndex((entry) => entry.id === patch.id);
      if (index >= 0) {
        state.conversations.splice(index, 1, { ...state.conversations[index], ...patch });
        state.conversations = sortConversations(state.conversations);
      }
      if (state.details[patch.id]) state.details = { ...state.details, [patch.id]: { ...state.details[patch.id], ...patch } };
    },
    [REMOVE_CHAT_CONVERSATION]: (state, id: string) => {
      state.conversations = state.conversations.filter((entry) => entry.id !== id);
      const details = { ...state.details };
      delete details[id];
      state.details = details;
      const messages = { ...state.messages };
      delete messages[id];
      state.messages = messages;
    },
    [SET_CHAT_MESSAGES_LOADING]: (state, payload: { id: string; loading: boolean }) => {
      const page = state.messages[payload.id] ?? emptyPage();
      state.messages = { ...state.messages, [payload.id]: { ...page, loading: payload.loading } };
    },
    [SET_CHAT_MESSAGES]: (state, payload: { id: string; messages: ChatMessageDto[]; hasMore: boolean; cursor: string | null }) => {
      const page = state.messages[payload.id] ?? emptyPage();
      // keep bubbles that are still pending or failed locally
      const local = page.items.filter((message) => message.pending || message.failed);
      state.messages = {
        ...state.messages,
        [payload.id]: { items: mergeMessages(local, payload.messages), hasMore: payload.hasMore, cursor: payload.cursor, loaded: true, loading: false },
      };
    },
    [PREPEND_CHAT_MESSAGES]: (state, payload: { id: string; messages: ChatMessageDto[]; hasMore: boolean; cursor: string | null }) => {
      const page = state.messages[payload.id] ?? emptyPage();
      state.messages = {
        ...state.messages,
        [payload.id]: { items: mergeMessages(page.items, payload.messages), hasMore: payload.hasMore, cursor: payload.cursor, loaded: true, loading: false },
      };
    },
    [APPEND_CHAT_MESSAGE]: (state, message: ChatMessageDto) => {
      const page = state.messages[message.conversationId];
      if (!page) return;
      state.messages = { ...state.messages, [message.conversationId]: { ...page, items: mergeMessages(page.items, [message]) } };
    },
    [PATCH_CHAT_MESSAGE]: (state, payload: { conversationId: string; id?: string; clientId?: string; patch: Partial<ChatMessageDto> }) => {
      const page = state.messages[payload.conversationId];
      if (!page) return;
      const items = page.items.map((message) => {
        const hit = (payload.id && message.id === payload.id) || (payload.clientId && message.clientId === payload.clientId);
        return hit ? { ...message, ...payload.patch } : message;
      });
      state.messages = { ...state.messages, [payload.conversationId]: { ...page, items: items.sort(byTime) } };
    },
    [REMOVE_CHAT_MESSAGE]: (state, payload: { conversationId: string; id?: string; clientId?: string }) => {
      const page = state.messages[payload.conversationId];
      if (!page) return;
      const items = page.items.filter((message) => !((payload.id && message.id === payload.id) || (payload.clientId && message.clientId === payload.clientId)));
      state.messages = { ...state.messages, [payload.conversationId]: { ...page, items } };
    },
    [SET_CHAT_MEMBER_READ]: (state, payload: { conversationId: string; employeeId: string; lastReadAt: string }) => {
      state.reads = { ...state.reads, [payload.conversationId]: { ...(state.reads[payload.conversationId] ?? {}), [payload.employeeId]: payload.lastReadAt } };
      const details = state.details[payload.conversationId];
      if (details?.members) {
        details.members = details.members.map((member) => (member.employeeId === payload.employeeId ? { ...member, lastReadAt: payload.lastReadAt } : member));
      }
    },
    [SET_CHAT_DIRECTORY]: (state, payload: { entries?: ChatDirectoryEntry[]; loading?: boolean }) => {
      if (payload.entries) {
        state.directory = payload.entries;
        state.directoryLoadedAt = Date.now();
      }
      if (payload.loading !== undefined) state.directoryLoading = payload.loading;
    },
    [SET_CHAT_NOTIFICATIONS]: (state, payload: { notifications: ChatNotificationDto[]; unseenCount: number; unreadCount: number }) => {
      state.notifications = payload.notifications;
      state.unseenCount = payload.unseenCount;
      state.unreadNotificationCount = payload.unreadCount;
      state.notificationsLoaded = true;
    },
    [UPSERT_CHAT_NOTIFICATION]: (state, notification: ChatNotificationDto) => {
      const index = state.notifications.findIndex((entry) => entry.id === notification.id);
      const previous = index >= 0 ? state.notifications[index] : null;
      if (index >= 0) state.notifications.splice(index, 1);
      state.notifications.unshift(notification);
      if (state.notifications.length > 50) state.notifications.length = 50;
      if (!notification.isSeen && (!previous || previous.isSeen)) state.unseenCount += 1;
      if (!notification.isRead && (!previous || previous.isRead)) state.unreadNotificationCount += 1;
    },
    [SET_CHAT_NOTIFICATION_COUNTS]: (state, payload: { unseenCount?: number; unreadCount?: number }) => {
      if (payload.unseenCount !== undefined) state.unseenCount = payload.unseenCount;
      if (payload.unreadCount !== undefined) state.unreadNotificationCount = payload.unreadCount;
    },
    /** Marks entries seen and/or read: all, by id, or by conversation. */
    [MARK_CHAT_NOTIFICATIONS]: (state, payload: { all?: boolean; ids?: string[]; conversationId?: string; seen?: boolean; read?: boolean }) => {
      state.notifications = state.notifications.map((entry) => {
        const hit = payload.all || (payload.ids && payload.ids.includes(entry.id)) || (payload.conversationId && entry.conversationId === payload.conversationId);
        if (!hit) return entry;
        return { ...entry, isSeen: entry.isSeen || !!payload.seen || !!payload.read, isRead: entry.isRead || !!payload.read };
      });
      if (payload.all) {
        if (payload.seen || payload.read) state.unseenCount = 0;
        if (payload.read) state.unreadNotificationCount = 0;
      } else {
        state.unseenCount = Math.min(state.unseenCount, state.notifications.filter((entry) => !entry.isSeen).length);
        state.unreadNotificationCount = Math.min(state.unreadNotificationCount, state.notifications.filter((entry) => !entry.isRead).length);
      }
    },
    [SET_CHAT_PRESENCE]: (state, payload: { list?: string[]; employeeId?: string; online?: boolean }) => {
      if (payload.list) {
        const presence: Record<string, boolean> = {};
        payload.list.forEach((id) => { presence[id] = true; });
        state.presence = presence;
      } else if (payload.employeeId) {
        const presence = { ...state.presence };
        if (payload.online) presence[payload.employeeId] = true;
        else delete presence[payload.employeeId];
        state.presence = presence;
      }
    },
    [SET_CHAT_TYPING]: (state, payload: { conversationId: string; employeeId: string; name: string; isTyping: boolean }) => {
      const current = { ...(state.typing[payload.conversationId] ?? {}) };
      if (payload.isTyping) current[payload.employeeId] = { name: payload.name, until: Date.now() + TYPING_TTL_MS };
      else delete current[payload.employeeId];
      state.typing = { ...state.typing, [payload.conversationId]: current };
    },
    [SET_CHAT_SOCKET]: (state, connected: boolean) => {
      state.socketConnected = connected;
      if (connected) state.everConnected = true;
    },
    [SET_CHAT_ACTIVE]: (state, id: string | null) => {
      state.activeConversationId = id;
    },
    [SET_CHAT_SUMMARY]: (state, summary: { unreadTotal: number; mentionTotal: number }) => {
      state.summary = summary;
    },
    [RESET_CHAT]: (state) => {
      Object.assign(state, initialState());
    },
  },

  actions: {
    [GET_CHAT_CONVERSATIONS]: async ({ commit }) => {
      commit(SET_CHAT_LOADING, true);
      const result: AxiosResponse = await Chat.getConversations();
      commit(SET_CHAT_LOADING, false);
      if (ok(result)) {
        commit(SET_CHAT_CONVERSATIONS, result.data.data.conversations ?? []);
        commit(SET_CHAT_SUMMARY, result.data.data.summary ?? { unreadTotal: 0, mentionTotal: 0 });
        commit(SET_CHAT_PRESENCE, { list: result.data.data.onlineEmployeeIds ?? [] });
        return true;
      }
      if (result?.data?.msg) commit(SET_ERRORMSG, result.data.msg);
      return false;
    },

    [GET_CHAT_CONVERSATION]: async ({ commit }, id: string) => {
      const result: AxiosResponse = await Chat.getConversation(id);
      if (ok(result)) {
        commit(UPSERT_CHAT_CONVERSATION, result.data.data);
        return result.data.data as ChatConversationDto;
      }
      return null;
    },

    [OPEN_CHAT_DIRECT]: async ({ commit }, employeeId: string) => {
      const result: AxiosResponse = await Chat.createDirect(employeeId);
      if (ok(result)) {
        commit(UPSERT_CHAT_CONVERSATION, result.data.data);
        return result.data.data as ChatConversationDto;
      }
      return null;
    },

    [CREATE_CHAT_GROUP]: async ({ commit }, payload: CreateChatGroup) => {
      const result: AxiosResponse = await Chat.createGroup(payload);
      if (ok(result)) {
        commit(UPSERT_CHAT_CONVERSATION, result.data.data);
        return result.data.data as ChatConversationDto;
      }
      return null;
    },

    [UPDATE_CHAT_CONVERSATION]: async ({ commit }, payload: { id: string; name?: string; description?: string }) => {
      const { id, ...changes } = payload;
      const result: AxiosResponse = await Chat.updateConversation(id, changes);
      if (ok(result)) {
        commit(UPSERT_CHAT_CONVERSATION, result.data.data);
        return result.data.data as ChatConversationDto;
      }
      return null;
    },

    [ADD_CHAT_MEMBERS]: async ({ commit }, payload: { id: string; memberIds: string[] }) => {
      const result: AxiosResponse = await Chat.addMembers(payload.id, payload.memberIds);
      if (ok(result)) {
        commit(UPSERT_CHAT_CONVERSATION, result.data.data);
        return result.data.data as ChatConversationDto;
      }
      return null;
    },

    [REMOVE_CHAT_MEMBER]: async ({ commit }, payload: { id: string; employeeId: string }) => {
      const result: AxiosResponse = await Chat.removeMember(payload.id, payload.employeeId);
      if (ok(result)) {
        commit(UPSERT_CHAT_CONVERSATION, result.data.data);
        return result.data.data as ChatConversationDto;
      }
      return null;
    },

    [LEAVE_CHAT_CONVERSATION]: async ({ commit }, id: string) => {
      const result: AxiosResponse = await Chat.leaveConversation(id);
      if (ok(result)) {
        commit(REMOVE_CHAT_CONVERSATION, id);
        return true;
      }
      return false;
    },

    [MUTE_CHAT_CONVERSATION]: async ({ commit }, payload: { id: string; isMuted: boolean }) => {
      const result: AxiosResponse = await Chat.setMuted(payload.id, payload.isMuted);
      if (ok(result)) {
        commit(PATCH_CHAT_CONVERSATION, { id: payload.id, isMuted: result.data.data.isMuted });
        return true;
      }
      return false;
    },

    [MARK_CHAT_READ]: async ({ commit }, id: string) => {
      const now = new Date().toISOString();
      commit(PATCH_CHAT_CONVERSATION, { id, unreadCount: 0, mentionCount: 0, lastReadAt: now });
      commit(MARK_CHAT_NOTIFICATIONS, { conversationId: id, read: true });
      closeDesktopNotification(`chat-${id}`);
      try {
        await Chat.markRead(id);
      } catch (e) {
        // the next open of the thread repeats the call
      }
      return true;
    },

    [GET_CHAT_MESSAGES]: async ({ commit, state }, payload: { id: string; before?: string | null }) => {
      const page = state.messages[payload.id] ?? emptyPage();
      if (page.loading) return false;
      commit(SET_CHAT_MESSAGES_LOADING, { id: payload.id, loading: true });
      const result: AxiosResponse = await Chat.getMessages(payload.id, { before: payload.before ?? null });
      if (ok(result)) {
        const data = result.data.data;
        const messages: ChatMessageDto[] = [...(data.messages ?? [])].reverse();
        commit(payload.before ? PREPEND_CHAT_MESSAGES : SET_CHAT_MESSAGES, { id: payload.id, messages, hasMore: !!data.hasMore, cursor: data.nextCursor ?? null });
        return true;
      }
      commit(SET_CHAT_MESSAGES_LOADING, { id: payload.id, loading: false });
      return false;
    },

    /** Shows the bubble at once, then swaps it for the stored message (or marks it failed). */
    [SEND_CHAT_MESSAGE]: async ({ commit, rootGetters }, payload: SendChatMessage & { clientId: string }) => {
      const me = rootGetters.account?.data;
      const optimistic: ChatMessageDto = {
        id: payload.clientId,
        clientId: payload.clientId,
        conversationId: payload.conversationId,
        senderId: me?.id ?? null,
        sender: me ? { id: me.id, fname: me.fname, lname: me.lname, profilePhoto: me.profilePhoto ?? null, role: me.role, isActive: true } : null,
        type: payload.file ? 'file' : 'text',
        body: payload.body ?? null,
        mentions: [],
        file: payload.file ? { key: payload.file.key, name: payload.file.name, size: payload.file.size, mime: payload.file.mime } : null,
        replyToId: payload.replyToId ?? null,
        replyTo: payload.replyTo ?? null,
        isDeleted: false,
        createdAt: new Date().toISOString(),
        pending: true,
        failed: false,
      };
      commit(APPEND_CHAT_MESSAGE, optimistic);

      try {
        const result: AxiosResponse = await Chat.sendMessage(payload);
        if (ok(result)) {
          const stored: ChatMessageDto = { ...result.data.data, clientId: payload.clientId, pending: false, failed: false };
          commit(APPEND_CHAT_MESSAGE, stored);
          commit(PATCH_CHAT_CONVERSATION, { id: payload.conversationId, lastMessageAt: stored.createdAt, lastMessagePreview: stored.file ? `📎 ${stored.file.name}` : plainBody(stored.body), lastMessageSenderId: stored.senderId });
          return stored;
        }
        commit(PATCH_CHAT_MESSAGE, { conversationId: payload.conversationId, clientId: payload.clientId, patch: { pending: false, failed: true } });
        if (result?.data?.msg) commit(SET_ERRORMSG, result.data.msg);
        return null;
      } catch (error: any) {
        commit(PATCH_CHAT_MESSAGE, { conversationId: payload.conversationId, clientId: payload.clientId, patch: { pending: false, failed: true } });
        const message = error?.response?.data?.message;
        if (message) commit(SET_ERRORMSG, message);
        return null;
      }
    },

    [RETRY_CHAT_MESSAGE]: async ({ commit, dispatch, state }, payload: { conversationId: string; clientId: string }) => {
      const page = state.messages[payload.conversationId];
      const message = page?.items.find((entry) => entry.clientId === payload.clientId);
      if (!message) return null;
      commit(REMOVE_CHAT_MESSAGE, { conversationId: payload.conversationId, clientId: payload.clientId });
      return dispatch(SEND_CHAT_MESSAGE, {
        conversationId: payload.conversationId,
        clientId: payload.clientId,
        body: message.body ?? undefined,
        file: message.file ? { key: message.file.key, name: message.file.name, size: message.file.size, mime: message.file.mime } : undefined,
        replyToId: message.replyTo?.id,
        replyTo: message.replyTo ?? null,
      });
    },

    [DISCARD_CHAT_MESSAGE]: ({ commit }, payload: { conversationId: string; clientId: string }) => {
      commit(REMOVE_CHAT_MESSAGE, { conversationId: payload.conversationId, clientId: payload.clientId });
    },

    [DELETE_CHAT_MESSAGE]: async ({ commit }, payload: { conversationId: string; messageId: string }) => {
      const result: AxiosResponse = await Chat.deleteMessage(payload.conversationId, payload.messageId);
      if (ok(result)) {
        commit(PATCH_CHAT_MESSAGE, { conversationId: payload.conversationId, id: payload.messageId, patch: { isDeleted: true, body: null, file: null, mentions: [] } });
        return true;
      }
      return false;
    },

    [GET_CHAT_DIRECTORY]: async ({ commit, state }, payload: { force?: boolean } = {}) => {
      if (!payload.force && state.directory.length && Date.now() - state.directoryLoadedAt < DIRECTORY_TTL_MS) return true;
      if (state.directoryLoading) return true;
      commit(SET_CHAT_DIRECTORY, { loading: true });
      const result: AxiosResponse = await Chat.getDirectory();
      if (ok(result)) {
        commit(SET_CHAT_DIRECTORY, { entries: result.data.data ?? [], loading: false });
        return true;
      }
      commit(SET_CHAT_DIRECTORY, { loading: false });
      return false;
    },

    [GET_CHAT_NOTIFICATIONS]: async ({ commit }) => {
      try {
        const result: AxiosResponse = await Chat.getNotifications();
        if (ok(result)) {
          commit(SET_CHAT_NOTIFICATIONS, { notifications: result.data.data.notifications ?? [], unseenCount: result.data.data.unseenCount ?? 0, unreadCount: result.data.data.unreadCount ?? 0 });
          return true;
        }
      } catch (e) {
        // silent request: nothing to show
      }
      return false;
    },

    [MARK_CHAT_NOTIFICATIONS_SEEN]: async ({ commit, state }) => {
      if (state.unseenCount === 0 && state.notifications.every((entry) => entry.isSeen)) return true;
      commit(MARK_CHAT_NOTIFICATIONS, { all: true, seen: true });
      try {
        await Chat.markNotificationsSeen();
      } catch (e) {
        // badge will resync on the next fetch
      }
      return true;
    },

    [MARK_CHAT_NOTIFICATIONS_READ]: async ({ commit }, payload: { ids?: string[]; all?: boolean } = {}) => {
      commit(MARK_CHAT_NOTIFICATIONS, { ...(payload.all || !payload.ids?.length ? { all: true } : { ids: payload.ids }), read: true });
      try {
        await Chat.markNotificationsRead(payload.all || !payload.ids?.length ? { all: true } : { ids: payload.ids });
      } catch (e) {
        // resynced on the next fetch
      }
      return true;
    },

    [GET_CHAT_UNREAD]: async ({ commit }) => {
      try {
        const result: AxiosResponse = await Chat.getUnread();
        if (ok(result)) {
          commit(SET_CHAT_SUMMARY, { unreadTotal: result.data.data.unreadTotal ?? 0, mentionTotal: result.data.data.mentionTotal ?? 0 });
          commit(SET_CHAT_NOTIFICATION_COUNTS, { unseenCount: result.data.data.unseenNotifications ?? 0 });
          return true;
        }
      } catch (e) {
        // silent
      }
      return false;
    },

    [SET_ACTIVE_CHAT_CONVERSATION]: ({ commit }, id: string | null) => {
      commit(SET_CHAT_ACTIVE, id);
    },

    [CLEAR_CHAT]: ({ commit }) => {
      typingTimers.forEach((timer) => clearTimeout(timer));
      typingTimers.clear();
      commit(RESET_CHAT);
      reflectUnreadInTitle(0);
    },

    /** Everything the socket delivers lands here. */
    [CHAT_SOCKET_EVENT]: async ({ commit, dispatch, state, getters, rootGetters }, { event, payload }: { event: string; payload: any }) => {
      const me: string | null = rootGetters.account?.data?.id ?? null;
      const viewing = (conversationId: string) =>
        state.activeConversationId === conversationId && typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus();

      switch (event) {
        case 'connect': {
          const reconnect = state.everConnected;
          commit(SET_CHAT_SOCKET, true);
          if (reconnect) {
            // Anything that happened while offline is picked up again from the API.
            if (state.conversationsLoaded) dispatch(GET_CHAT_CONVERSATIONS);
            else dispatch(GET_CHAT_UNREAD);
            if (state.notificationsLoaded) dispatch(GET_CHAT_NOTIFICATIONS);
            if (state.activeConversationId && state.messages[state.activeConversationId]?.loaded) dispatch(GET_CHAT_MESSAGES, { id: state.activeConversationId });
          }
          break;
        }
        case 'disconnect':
          commit(SET_CHAT_SOCKET, false);
          break;

        case 'message:new': {
          const message: ChatMessageDto = payload.message;
          const summary = payload.conversation ?? {};
          const known = state.conversations.find((entry) => entry.id === message.conversationId);
          const mine = !!me && message.senderId === me;

          if (!known) {
            if (state.conversationsLoaded) await dispatch(GET_CHAT_CONVERSATION, message.conversationId);
          } else {
            const patch: Partial<ChatConversationDto> & { id: string } = {
              id: message.conversationId,
              lastMessageAt: summary.lastMessageAt ?? message.createdAt,
              lastMessagePreview: summary.lastMessagePreview ?? null,
              lastMessageSenderId: summary.lastMessageSenderId ?? message.senderId,
            };
            if (!mine && message.type !== 'system' && !viewing(message.conversationId)) {
              patch.unreadCount = (known.unreadCount || 0) + 1;
              if (me && message.mentions?.includes(me)) patch.mentionCount = (known.mentionCount || 0) + 1;
            }
            commit(PATCH_CHAT_CONVERSATION, patch);
          }
          commit(APPEND_CHAT_MESSAGE, message);
          commit(SET_CHAT_TYPING, { conversationId: message.conversationId, employeeId: message.senderId ?? '', name: '', isTyping: false });

          if (!mine && message.type !== 'system') {
            if (viewing(message.conversationId)) {
              dispatch(MARK_CHAT_READ, message.conversationId);
            } else {
              const conversation = getters.chatConversationById(message.conversationId) as ChatConversationDto | null;
              const muted = !!conversation?.isMuted;
              const mentioned = !!me && !!message.mentions?.includes(me);
              if (!muted || mentioned) {
                playChime();
                const title = conversation?.type === 'direct' || !conversation
                  ? employeeDisplayName(message.sender)
                  : `${employeeDisplayName(message.sender)} · ${conversationTitle(conversation)}`;
                showDesktopNotification({
                  tag: `chat-${message.conversationId}`,
                  title: mentioned ? `${employeeDisplayName(message.sender)} mentioned you` : title,
                  body: summary.lastMessagePreview ?? plainBody(message.body) ?? '',
                  onClick: () => router.push({ name: CHAT_CONVERSATION_ROUTE, params: { id: message.conversationId } }),
                });
              }
            }
          }
          break;
        }

        case 'message:deleted':
          commit(PATCH_CHAT_MESSAGE, { conversationId: payload.conversationId, id: payload.messageId, patch: { isDeleted: true, body: null, file: null, mentions: [] } });
          if (payload.lastMessagePreview) commit(PATCH_CHAT_CONVERSATION, { id: payload.conversationId, lastMessagePreview: payload.lastMessagePreview });
          break;

        case 'conversation:updated':
          if (payload?.conversation?.id) commit(PATCH_CHAT_CONVERSATION, payload.conversation);
          break;

        case 'conversation:added':
          if (payload?.conversation) {
            commit(UPSERT_CHAT_CONVERSATION, payload.conversation);
            playChime();
          }
          break;

        case 'conversation:removed':
          commit(REMOVE_CHAT_CONVERSATION, payload.conversationId);
          break;

        case 'conversation:read':
          if (payload.employeeId === me) {
            commit(PATCH_CHAT_CONVERSATION, { id: payload.conversationId, unreadCount: 0, mentionCount: 0, lastReadAt: payload.lastReadAt });
            commit(MARK_CHAT_NOTIFICATIONS, { conversationId: payload.conversationId, read: true });
          } else {
            commit(SET_CHAT_MEMBER_READ, { conversationId: payload.conversationId, employeeId: payload.employeeId, lastReadAt: payload.lastReadAt });
          }
          break;

        case 'conversation:muted':
          commit(PATCH_CHAT_CONVERSATION, { id: payload.conversationId, isMuted: !!payload.isMuted });
          break;

        case 'notification:new': {
          const notification: ChatNotificationDto = payload.notification;
          if (viewing(notification.conversationId)) {
            commit(UPSERT_CHAT_NOTIFICATION, { ...notification, isSeen: true, isRead: true });
          } else {
            commit(UPSERT_CHAT_NOTIFICATION, notification);
          }
          break;
        }

        case 'notification:read':
          if (payload.all) commit(MARK_CHAT_NOTIFICATIONS, { all: true, read: true });
          else if (payload.ids) commit(MARK_CHAT_NOTIFICATIONS, { ids: payload.ids, read: true });
          else if (payload.conversationId) commit(MARK_CHAT_NOTIFICATIONS, { conversationId: payload.conversationId, read: true });
          break;

        case 'notification:seen':
          commit(MARK_CHAT_NOTIFICATIONS, { all: true, seen: true });
          break;

        case 'presence:list':
          commit(SET_CHAT_PRESENCE, { list: payload.employeeIds ?? [] });
          break;

        case 'presence':
          commit(SET_CHAT_PRESENCE, { employeeId: payload.employeeId, online: !!payload.online });
          break;

        case 'typing': {
          if (payload.employeeId === me) break;
          commit(SET_CHAT_TYPING, { conversationId: payload.conversationId, employeeId: payload.employeeId, name: payload.name, isTyping: !!payload.isTyping });
          const key = `${payload.conversationId}:${payload.employeeId}`;
          const previous = typingTimers.get(key);
          if (previous) clearTimeout(previous);
          if (payload.isTyping) {
            typingTimers.set(key, setTimeout(() => {
              typingTimers.delete(key);
              commit(SET_CHAT_TYPING, { conversationId: payload.conversationId, employeeId: payload.employeeId, name: payload.name, isTyping: false });
            }, TYPING_TTL_MS));
          }
          break;
        }

        default:
          break;
      }

      reflectUnreadInTitle(getters.chatUnreadTotal);
    },
  },
};

export default chat;
